/**
 * Финальный справочник интересов заказчика — источник каталога «Показать все»
 * и плоского списка ALL_INTERESTS.
 *
 * Значения перенесены дословно из `artifacts/interests-catalog.json`
 * (115 интересов, 5 групп). Порядок групп и порядок значений внутри группы —
 * как в файле; числовые префиксы («1. ») в UI не выводятся.
 */
export interface InterestGroup {
    /** Заголовок группы в каталоге. */
    title: string;
    items: readonly string[];
}

/**
 * `as const satisfies` — не украшение: `satisfies` проверяет форму каталога, а
 * `as const` сохраняет литеральные типы значений, из которых ниже выводится
 * CatalogInterest. Через него типизирован INTEREST_OPTIONS — «предлагаемые»
 * теперь не могут разъехаться с каталогом: значение вне справочника не
 * пройдёт tsc, а значит и CI.
 */
export const INTEREST_GROUPS = [
    {
        title: "Спорт и активность",
        items: [
            "Бег",
            "Плавание",
            "Йога",
            "Велоспорт",
            "Спортзал",
            "Лыжи",
            "Сноуборд",
            "Танцы",
            "Боевые искусства",
            "Серфинг",
            "Хайкинг",
            "Теннис",
            "Скалолазание",
            "Гольф",
            "Кёрлинг",
            "Конный спорт",
            "Дартс",
            "Гимнастика",
            "Парашютный спорт",
            "Гребля",
            "Подводное плавание",
            "Каякинг",
            "Бокс",
            "Бейсбол",
            "Регби",
            "Хоккей",
            "Лёгкая атлетика",
            "Ролики",
            "Настольный теннис",
            "Пляжный волейбол",
            "Скейтбординг",
            "Бадминтон",
            "Футбол",
            "Баскетбол",
            "Пилатес",
            "Стретчинг",
            "Автоспорт",
        ],
    },
    {
        title: "Творчество и культура",
        items: [
            "Музыка",
            "Игра на инструментах",
            "Винил",
            "Литература",
            "Театр",
            "Кино",
            "Фотография",
            "Рисование",
            "Рукоделие",
            "Дизайн",
            "Писательство",
            "Керамика",
            "Диджеинг",
            "Тату",
            "Косплей",
            "Моделинг",
            "Ораторское искусство",
            "Астрология",
        ],
    },
    {
        title: "Развлечения и тусовки",
        items: [
            "Видеоигры",
            "Настольные игры",
            "Ретро-игры",
            "Шахматы",
            "Сериалы",
            "Аниме",
            "Подкасты",
            "Концерты",
            "Музыкальные фестивали",
            "Караоке",
            "Стендап",
            "Квизы",
            "Бары и ночная жизнь",
            "Вечеринки",
            "Музеи",
            "Встречи с друзьями",
        ],
    },
    {
        title: "Еда, путешествия и лайфстайл",
        items: [
            "Кулинария",
            "Кофе",
            "Вино",
            "Крафтовое пиво",
            "Рестораны",
            "Гастротуризм",
            "ЗОЖ-питание",
            "Путешествия",
            "Кемпинг",
            "Культура других стран",
            "Автомобили и мотоциклы",
            "Мода",
            "Уход за собой и ЗОЖ",
            "Астрономия",
            "Садоводство",
            "Рыбалка",
            "Коллекционирование",
            "Кошки",
            "Собаки",
            "Питомцы",
            "Прогулки с собаками",
        ],
    },
    {
        title: "Ценности и саморазвитие",
        items: [
            "Волонтёрство",
            "Благотворительность",
            "Экоактивизм",
            "Защита животных",
            "Помощь пожилым",
            "Помощь бездомным",
            "Работа с детьми",
            "Социальные проекты",
            "Образовательные проекты",
            "Психология",
            "Философия",
            "Медитация",
            "Изучение языков",
            "Самообразование",
            "Научные лекции",
            "Онлайн-курсы",
            "История",
            "Технологии",
            "Нейросети",
            "Программирование",
            "Финансовая грамотность",
            "Инвестиции",
            "Лидерские навыки",
        ],
    },
] as const satisfies readonly InterestGroup[];

/** Любое значение из финального каталога. Вне каталога — ошибка компиляции. */
export type CatalogInterest = (typeof INTEREST_GROUPS)[number]["items"][number];

/**
 * Значения из прежнего справочника, которых нет в финальном каталоге.
 *
 * В каталоге «Показать все» они не показываются, но остаются допустимыми:
 * такие интересы уже сохранены в профилях, и без этого списка они перестали бы
 * считаться известными — не подсвечивались бы как выбранные и не проходили бы
 * проверку при вводе. Список не пополняется: он существует ради старых данных.
 */
export const LEGACY_INTERESTS: string[] = [
    "Фитнес",
    "Походы",
    "Велосипед",
    "Искусство",
    "Автомобили",
    "Наука",
    "Спорт",
    "Книги",
    "Животные",
    "Природа",
];
